/**
 * CreateParticipantsStep Component
 * Displays friends/groups selection step with shared layout.
 * Uses shared FriendsGroupsToggle and FriendsFilterButtons (same as Friends tab).
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  View, Text, Dimensions, Platform, Pressable,
} from 'react-native'
import { WEB_HOVER_TRANSITION } from '../../../src/shared/constants/animations'
import { debounce } from '../../../src/shared/utils/debounce'
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext'
import { useWalkthroughStep } from '../../../src/shared/hooks/useWalkthroughStep'
import { useFirstChallengeTarget } from '../../../src/onboarding/hooks/useFirstChallengeTarget'
import { FC_TARGET_KEYS } from '../../../src/onboarding/constants/firstChallengeTour.constants'
import { MONKEY_USER_ID } from '../../../constants/monkey'
import { OPEN_CHALLENGE_PARTICIPANT_ID } from '../../../src/create/constants/create.constants'
import { useParticipantFilters } from '../../../src/create/hooks/useParticipantFilters'
import { useFriendData } from '../../../src/friends/hooks/useFriendData'
import { useGroupsData } from '../../../src/friends/hooks/useGroupsData'
import { useReceivedInvitesCount } from '../../../src/friends/hooks/useReceivedInvitesCount'
import { FriendSelectorList } from '../../friends/FriendSelectorList'
import { FriendsGrid } from '../../friends/FriendsList/FriendsGrid'
import { FriendsGridSkeleton } from '../../friends/FriendsList/FriendsGridSkeleton'
import { FriendCard } from '../FriendCard/FriendCard'
import { RivalCard } from '../RivalCard/RivalCard'
import { getRivalCardHeight } from '../RivalCard/RivalCard.styles'
import { InviteCard } from '../../friends/InviteCard/InviteCard'
import { GroupCard } from '../../friends/GroupCard/GroupCard'
import { AddGroupCard } from '../../friends/AddGroupCard/AddGroupCard'
import { CREATE_GROUP_BUTTON_ITEM } from '../../../src/friends/helpers/unifiedList.helper'
import { FriendsGroupsToggle } from '../../friends/FriendsGroupsToggle/FriendsGroupsToggle'
import { FriendsFilterButtons } from '../../friends/FriendsFilterButtons/FriendsFilterButtons'
import { InviteFriendsButton } from '../../friends/InviteFriendsButton'
import { CreateGroupModal } from '../../friends/CreateGroupModal/CreateGroupModal'
import { useFriendsPage } from '../../../src/friends/contexts/FriendsPageContext'
import { getCreateParticipantsStepStyles } from './CreateParticipantsStep.styles'
import { useTheme } from '../../../src/theme/useTheme'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics/events'
import { VIEW_TYPE } from '../../../src/friends/constants/viewFilter.constants'
import type { Group } from '../../../src/friends/types/groups.types'
import { GROUP_LIST_ITEM_TYPE } from '../../../src/friends/constants/groups.constants'
import { FRIENDS_CONTENT_PADDING_HORIZONTAL } from '../../../src/friends/styles/friends.styles'
import { calculateColumns } from '../../../src/shared/helpers/calculateColumns.helper'
import { useGuestWalkthroughStore } from '../../../src/shared/stores/guestWalkthroughStore'
import { WALKTHROUGH_STEPS } from '../../../src/create/constants/guestWalkthrough.constants'
import type { ParticipantFilterType } from '../../../src/create/types/create.types'

export const CreateParticipantsStep: React.FC = () => {
  const theme = useTheme()
  const { track } = useAnalytics()
  const styles = useMemo(() => getCreateParticipantsStepStyles(theme), [theme])
  const {
    activeTab,
    setActiveTab,
    selectedFriendId,
    setSelectedFriendId,
    selectedGroupId,
    setSelectedGroupId,
    setSelectedGroup,
    availableGroups,
    groupsLoading,
    friends,
    gamesLoading,
    currentUserId,
    isGuestMonkeyCreateFlow,
    setStep,
  } = useCreatePage()
  const isWalkthroughActive = useGuestWalkthroughStore((s) => s.isActive)
  const walkthroughStep = useGuestWalkthroughStore((s) => s.currentStep)
  const advanceWalkthroughStep = useGuestWalkthroughStore((s) => s.advanceStep)

  const screenWidth = Dimensions.get('window').width
  const { items } = useFriendData({ friends, duplicateCount: 1 })
  const [numColumns, setNumColumns] = useState(() => calculateColumns(screenWidth))
  const [screenWidthState, setScreenWidthState] = useState(screenWidth)
  const widthStateRef = useRef(screenWidth)
  const columnStateRef = useRef(calculateColumns(screenWidth))
  const renderCountRef = useRef(0)
  renderCountRef.current += 1
  const contentWidth = screenWidthState - 2 * FRIENDS_CONTENT_PADDING_HORIZONTAL
  const [filterType, setFilterType] = useState<ParticipantFilterType>('FRIEND')
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const {
    setShowInviteModal,
    setInviteTab,
    refreshGroups,
    setSnackbar,
  } = useFriendsPage()
  const { validGroups } = useParticipantFilters({ friends, availableGroups })
  // Rival-first flow: no stake-based disabling here — the stake is chosen AFTER the rival
  // (Review screen), which warns + blocks Send when the opponent can't cover it (AC-4.4).
  // The old stake-first flow grayed poor friends out here, which wrongly persisted after
  // back-navigation or a previous challenge's stake.
  const excludeIds = useMemo(() =>
    (currentUserId ? [currentUserId] : []), 
    [currentUserId]
  );
  // During walkthrough PARTICIPANTS_PICK step, hide the visual selection until the user actually taps
  // the monkey (proxy tap → advanceStep → PARTICIPANTS_SEND). The underlying state still has monkey
  // selected so the rest of the flow works; this only suppresses the "marked" UI on that one step.
  const selectedIds = useMemo(
    () => {
      if (isWalkthroughActive && walkthroughStep === WALKTHROUGH_STEPS.PARTICIPANTS_PICK) {
        return new Set<string>()
      }
      return selectedFriendId ? new Set([selectedFriendId]) : new Set<string>()
    },
    [selectedFriendId, isWalkthroughActive, walkthroughStep]
  )
  // Rival-first flow: tapping an opponent selects it AND advances to the Build screen (AC-1.4).
  // Use setStep directly (not the guarded toBuild) so the just-set rival advances in the same tick.
  const handleToggleFriend = useCallback(
    (id: string) => {
      setSelectedFriendId(id)
      setStep('build')
    },
    [setSelectedFriendId, setStep]
  )
  const inviteAppendCard = useMemo(() => ({ type: 'INVITE' as const, id: 'invite-button' }), [])

  const { items: groupItems } = useGroupsData({ groups: validGroups, duplicateCount: 1 })
  const activeGroupItems = useMemo(
    () => groupItems.filter((item) => item.type === GROUP_LIST_ITEM_TYPE.GROUP_ACTIVE),
    [groupItems]
  )
  const groupActiveCount = useMemo(
    () =>
      groupItems.filter((item) => item.type === GROUP_LIST_ITEM_TYPE.GROUP_ACTIVE).length,
    [groupItems]
  )

  const { filteredItems, countsForFilter } = useMemo(() => {
    const requestItems: typeof items = []
    const friendItems: typeof items = []
    const pendingItems: typeof items = []
    for (const item of items) {
      if (item.type === 'REQUEST') requestItems.push(item)
      else if (item.type === 'FRIEND') friendItems.push(item)
      else if (item.type === 'PENDING') pendingItems.push(item)
    }
    const friendCount = friendItems.length
    const pendingCount = requestItems.length + pendingItems.length
    return {
      countsForFilter:
        activeTab === 'friends'
          ? { friend: friendCount, pending: pendingCount }
          : { friend: groupActiveCount, pending: 0 },
      filteredItems:
        filterType === 'FRIEND'
          ? friendItems
          : [...requestItems, ...pendingItems],
    }
  }, [items, filterType, activeTab, groupActiveCount])

  const debouncedUpdateRef = useRef(
    debounce((width: number) => {
      const normalizedWidth = Math.round(width)
      const nextColumns = calculateColumns(normalizedWidth)
      const widthChanged = normalizedWidth !== widthStateRef.current
      const columnsChanged = nextColumns !== columnStateRef.current

      if (!widthChanged && !columnsChanged) return

      widthStateRef.current = normalizedWidth
      columnStateRef.current = nextColumns
      setNumColumns(nextColumns)
      setScreenWidthState(normalizedWidth)
    }, Platform.OS === 'web' ? 150 : 0)
  )

  // Prevent self-challenge: clear selection if current user was previously selected (e.g. stale state)
  useEffect(() => {
    if (selectedFriendId && currentUserId && selectedFriendId === currentUserId) {
      setSelectedFriendId(null)
    }
  }, [currentUserId, selectedFriendId, setSelectedFriendId])

  useEffect(() => {
    const updateDimensions = () => {
      const { width } = Dimensions.get('window')
      const normalizedWidth = Math.round(width)
      if (Platform.OS === 'web') debouncedUpdateRef.current(normalizedWidth)
      else {
        const nextColumns = calculateColumns(normalizedWidth)
        const widthChanged = normalizedWidth !== widthStateRef.current
        const columnsChanged = nextColumns !== columnStateRef.current

        if (!widthChanged && !columnsChanged) return

        widthStateRef.current = normalizedWidth
        columnStateRef.current = nextColumns
        setNumColumns(nextColumns)
        setScreenWidthState(normalizedWidth)
      }
    }
    updateDimensions()
    const subscription = Dimensions.addEventListener('change', updateDimensions)
    const debouncedUpdate = debouncedUpdateRef.current
    return () => {
      subscription?.remove()
      debouncedUpdate.cancel()
    }
  }, [])

  useEffect(() => {
    if (!__DEV__) return
    if (renderCountRef.current % 25 !== 0) return

    // render count checkpoint (dev only, every 25 renders)
    void { renderCount: renderCountRef.current, activeTab, filterType, selectedFriendId, numColumns, screenWidthState }
  }, [activeTab, filterType, selectedFriendId, numColumns, screenWidthState])

  useEffect(() => {
    if (!isWalkthroughActive || walkthroughStep !== WALKTHROUGH_STEPS.PARTICIPANTS_PICK) return
    // Guest flow: monkey card is spotlighted; selection is auto-set, advance fires via proxy tap
    if (isGuestMonkeyCreateFlow) return
    if (!selectedFriendId && !selectedGroupId) return
    advanceWalkthroughStep()
  }, [
    isWalkthroughActive,
    walkthroughStep,
    isGuestMonkeyCreateFlow,
    selectedFriendId,
    selectedGroupId,
    advanceWalkthroughStep,
  ])

  // Spotlight the monkey card for everyone running the walkthrough. Always install a proxy:
  // for non-guests, the proxy selects the monkey explicitly (since the underlying card's
  // onPress can't fire — the modal/spotlight chrome sits above it on Android).
  const handleMonkeyProxyTap = useCallback(() => {
    if (!isGuestMonkeyCreateFlow) {
      setSelectedFriendId(MONKEY_USER_ID)
    }
    advanceWalkthroughStep()
  }, [isGuestMonkeyCreateFlow, setSelectedFriendId, advanceWalkthroughStep])

  const { viewRef: monkeyCardRef } = useWalkthroughStep(WALKTHROUGH_STEPS.PARTICIPANTS_PICK, {
    onProxyTap: handleMonkeyProxyTap,
    useAfterLayout: true,
  })

  // First-challenge tour: spotlight the Monkey card by measuring the SAME ref (no JSX change).
  useFirstChallengeTarget(FC_TARGET_KEYS.rival, monkeyCardRef)

  const viewType = activeTab === 'friends' ? VIEW_TYPE.FRIENDS : VIEW_TYPE.GROUPS
  const { friendReceived: friendReceivedCount, groupReceived: groupReceivedCount } =
    useReceivedInvitesCount()

  const handleViewTypeChange = useCallback(
    (newViewType: typeof viewType) => {
      if (isGuestMonkeyCreateFlow && newViewType === VIEW_TYPE.GROUPS) return
      if (newViewType === VIEW_TYPE.FRIENDS) {
        setActiveTab('friends')
        setSelectedGroupId(null)
        setSelectedGroup(null)
      } else {
        setActiveTab('groups')
        setSelectedFriendId(null)
      }
    },
    [
      isGuestMonkeyCreateFlow,
      setActiveTab,
      setSelectedGroupId,
      setSelectedGroup,
      setSelectedFriendId,
    ]
  )

  const handleFriendSelect = useCallback(
    (friendId: string) => {
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_PARTICIPANT_SELECTED, {
        participant_type: 'friend',
      })
      setSelectedFriendId(friendId)
      setStep('build')
    },
    [track, setSelectedFriendId, setStep]
  )

  // "Post to anyone": pick no named opponent — the challenge goes to the public board and the
  // first stranger to accept fills the slot. Rides the reserved OPEN id like the Monkey does.
  const handleSelectOpen = useCallback(() => {
    setSelectedGroupId(null)
    setSelectedGroup(null)
    setSelectedFriendId(OPEN_CHALLENGE_PARTICIPANT_ID)
    setStep('build')
  }, [setSelectedGroupId, setSelectedGroup, setSelectedFriendId, setStep])

  const handleGroupSelect = useCallback(
    (group: Group, groupId: string) => {
      const groupMembersCount = group.memberIds?.length ?? 0
      track(ANALYTICS_EVENTS.CREATE_CHALLENGE_PARTICIPANT_SELECTED, {
        participant_type: 'group',
        group_members_count: groupMembersCount,
      })
      setSelectedGroupId(groupId)
      setSelectedGroup(group)
      setStep('build')
    },
    [track, setSelectedGroupId, setSelectedGroup, setStep]
  )

  const renderGroupCard = useCallback(
    ({
      item,
      cardWidth,
    }: {
      item: { type: string; group?: Group; id: string }
      index: number
      cardWidth: number
    }) => {
      const group = 'group' in item ? item.group : undefined
      if (!group) return null
      return (
        <GroupCard
          group={group}
          cardWidth={cardWidth}
          onPress={() => handleGroupSelect(group, group.id)}
          isSelected={selectedGroupId === group.id}
        />
      )
    },
    [selectedGroupId, handleGroupSelect]
  )

  const handleInvitePress = useCallback(() => {
    track(ANALYTICS_EVENTS.INVITE_MODAL_OPENED, { source: 'create' })
    setShowInviteModal(true)
    setInviteTab('manual')
  }, [track, setShowInviteModal, setInviteTab])

  const handleCreateGroupPress = useCallback(() => {
    setShowCreateGroupModal(true)
  }, [])

  // Rival cards cap their image height, so their height isn't a fixed aspect ratio like the
  // Friends tab's cards. Size the Invite tile from the same helper so it matches them here.
  const renderInviteCard = useCallback(
    ({ cardWidth }: { cardWidth: number }) => (
      <InviteCard
        cardWidth={cardWidth}
        height={getRivalCardHeight(cardWidth)}
        onPress={handleInvitePress}
        testID="create-participants-friend-selector-invite-card"
      />
    ),
    [handleInvitePress]
  )

  const groupsGridItems = useMemo(
    () => [...activeGroupItems, CREATE_GROUP_BUTTON_ITEM],
    [activeGroupItems]
  )

  const renderGroupsGridCard = useCallback(
    ({
      item,
      cardWidth: w,
    }: {
      item: { type: string; group?: Group; id: string }
      index: number
      cardWidth: number
    }) => {
      if (item.type === 'CREATE_GROUP') {
        return (
          <AddGroupCard
            cardWidth={w}
            onPress={handleCreateGroupPress}
            testID="create-participants-add-group-card"
          />
        )
      }
      return renderGroupCard({ item, index: 0, cardWidth: w })
    },
    [renderGroupCard, handleCreateGroupPress]
  )

  const showEmptyFriends = filteredItems.length === 0
  const showGamesLoading = gamesLoading
  const showGroupsSkeleton = activeTab === 'groups' && groupsLoading

  return (
    <View
      style={styles.container}
      testID={activeTab === 'friends' ? 'create-participants-step-friends' : 'create-participants-step-groups'}
    >
      <Text style={styles.sectionTitle}>Pick your opponent</Text>

      {!isGuestMonkeyCreateFlow ? (
        <Pressable
          onPress={handleSelectOpen}
          testID="create-participants-open-box"
          accessibilityRole="button"
          accessibilityLabel="Post to anyone"
          style={({ pressed, hovered }) => [
            styles.openBox,
            Platform.select({ web: { transition: WEB_HOVER_TRANSITION } as object }),
            hovered && Platform.OS === 'web' && styles.openBoxHover,
            pressed && styles.openBoxPressed,
          ]}
        >
          <Text style={styles.openBoxIcon}>🎲</Text>
          <View style={styles.openBoxText}>
            <Text style={styles.openBoxTitle}>Post to anyone</Text>
            <Text style={styles.openBoxSub}>Let a stranger take you on</Text>
          </View>
        </Pressable>
      ) : null}

      <View style={styles.toggleWrapper}>
        <FriendsGroupsToggle
          viewType={viewType}
          onViewTypeChange={handleViewTypeChange}
          friendReceivedCount={friendReceivedCount}
          groupReceivedCount={groupReceivedCount}
          groupsTabDisabled={isGuestMonkeyCreateFlow}
          testID="create-participants-toggle"
        />
      </View>

      <View style={styles.filterRow}>
        <FriendsFilterButtons
          filterType={filterType}
          counts={countsForFilter}
          // No Suggested tab in the create flow (showSuggested defaults false); narrow to be safe.
          onFilterChange={(filter) => {
            if (filter !== 'SUGGESTED') setFilterType(filter)
          }}
          viewType={viewType}
          testID="create-participants-filters"
        />
        {!isGuestMonkeyCreateFlow && viewType === VIEW_TYPE.FRIENDS ? (
          <InviteFriendsButton
            label="Invite"
            onPress={handleInvitePress}
            testID="create-participants-invite-button"
          />
        ) : null}
        {!isGuestMonkeyCreateFlow && viewType === VIEW_TYPE.GROUPS ? (
          <InviteFriendsButton
            label="Create"
            onPress={() => setShowCreateGroupModal(true)}
            testID="create-participants-create-group-button"
          />
        ) : null}
      </View>

      <CreateGroupModal
        isVisible={showCreateGroupModal}
        onDismiss={() => setShowCreateGroupModal(false)}
        onGroupCreated={async () => {
          await refreshGroups()
        }}
        onShowMessage={(message, variant) =>
          setSnackbar({ message, variant })
        }
      />

      {showGamesLoading ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      ) : (
        <View style={styles.gridContentWrapper}>
          {showGroupsSkeleton ? (
            <FriendsGridSkeleton
              numColumns={numColumns}
              screenWidth={screenWidthState}
              contentWidth={contentWidth}
            />
          ) : activeTab === 'friends' ? (
            showEmptyFriends ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>No friends available</Text>
              </View>
            ) : filterType === 'FRIEND' ? (
              <FriendSelectorList
                selectedIds={selectedIds}
                onToggle={handleToggleFriend}
                excludeIds={excludeIds}
                multiSelect={false}
                layout="grid"
                gridNumColumns={numColumns}
                contentWidth={contentWidth}
                hideWinRate={false}
                showCoins
                compactText={false}
                hideSearch={true}
                appendCard={isGuestMonkeyCreateFlow ? undefined : inviteAppendCard}
                onAppendCardPress={isGuestMonkeyCreateFlow ? undefined : handleInvitePress}
                renderAppendCard={isGuestMonkeyCreateFlow ? undefined : renderInviteCard}
                walkthroughTargetFriendId={MONKEY_USER_ID}
                walkthroughCardRef={monkeyCardRef}
                renderFriendCard={({ friend, cardWidth, isSelected, isDisabled }) => (
                  <RivalCard
                    friend={friend}
                    cardWidth={cardWidth}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    testID={`create-rival-card-${friend.id}`}
                  />
                )}
                testID="create-participants-friend-selector"
              />
            ) : (
              <View style={styles.pendingListContainer}>
                {filteredItems.map((item) => {
                  const friend = item.friend
                  return (
                    <FriendCard
                      key={friend.id}
                      friend={friend}
                      isSelected={selectedFriendId === friend.id}
                      onSelect={() => handleFriendSelect(friend.id)}
                    />
                  )
                })}
              </View>
            )
          ) : (
            <FriendsGrid
              items={groupsGridItems}
              renderCard={renderGroupsGridCard}
              numColumns={numColumns}
              screenWidth={screenWidthState}
              contentWidth={contentWidth}
            />
          )}
        </View>
      )}
    </View>
  )
}
